import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { StaticImage } from '@nxte-nl/static-image';

import { nextRoutes } from '../../constants/nextRouting';
import {
  FeatureToggleWrapContainer,
} from '../../containers/FeatureToggleWrapContainer/FeatureToggleWrapContainer';
import { RedeemLinkContainer } from '../../containers/RedeemLinkContainer/RedeemLinkContainer';
import {
  StoreSwitcherButtonContainer,
} from '../../containers/StoreSwitcherButtonContainer/StoreSwitcherButtonContainer';
import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { routerHooks } from '../../hooks/routerHooks/routerHooks';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import { versionDependent } from '../../images/versionDependentExport';
import { FeatureToggleValues } from '../../types/FeatureToggles';
import { LayoutExtension } from '../../types/ResponsiveState';
import { ImagePresentational } from '../../types/common';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { HeaderDecoration } from '../HeaderDecoration/HeaderDecoration';
import { Typography } from '../Typography/Typography';
import { CircleWithQuestionMark } from '../icons';
import styles from './RedeemHeader.module.css';

const messages = defineMessages({
  customerSupportLink: {
    id: 'src.components.RedeemHeader.customerSupportLink',
    defaultMessage: 'Customer support',
  },
  delayWarning: {
    id: 'src.components.RedeemHeader.delayWarning',
    defaultMessage: 'Let op, digitale cadeaukaarten worden momenteel vertraagd verstuurd. Onze excuses voor het ongemak.',
  },
  logoImageAlt: {
    id: 'src.components.RedeemHeader.logoImageAlt',
    defaultMessage: 'Logo',
  },
});

export type Props = {
  informativeMessage?: string | null;
  isLogosFromCBEOn: boolean;
  layoutExtension: LayoutExtension;
  mainLogoFromCBE?: ImagePresentational | null;
  showDelayWarning: boolean;
  showInformativeMessage?: boolean;
};

export const hooks = {
  useIsShowLanguageSwitcher() {
    const pathname = routerHooks.usePathName();
    const intl = useIntl();
    return React.useMemo(() => {
      const redeemCheckBalance = intl.formatMessage(nextRoutes.redeemPageRoute);
      const pathNameWithSlashInTheEnd = pathname.endsWith('/') ? pathname
        : `${pathname}/`;
      const langRegExp = languageUtils.getLanguageRegularExpression();
      const match = pathNameWithSlashInTheEnd.match(langRegExp);
      if (match) {
        const slicedPath = pathNameWithSlashInTheEnd.replace(match[0], '');
        return `/${slicedPath}` === redeemCheckBalance;
      }
      return pathNameWithSlashInTheEnd === redeemCheckBalance;
    }, [intl, pathname]);
  },
};

export const RedeemHeader = React.memo<Props>((props: Props) => {
  const {
    layoutExtension,
    showDelayWarning,
    mainLogoFromCBE,
    isLogosFromCBEOn,
    showInformativeMessage,
    informativeMessage,
  } = props;
  const intl = useIntl();

  const customerSupportPageRedirect = navigationHooks.useOldCustomerSupportPageRedirect();

  const homePageRedirect = navigationHooks.useHomePageRedirect(true);
  const oldCBRedirect = navigationHooks.useOldCBRedirect();
  const storeSwitcherButtonLabel = storeSwitcherHooks.useGetStoreSwitcherButtonLabel({
    isOnlyLanguage: true,
  });

  const isShowLanguageSwitcher = hooks.useIsShowLanguageSwitcher();

  const { isClient } = hydrationHooks.useIsClient();

  return (
    <div className={styles.rootContainer}>
      {showInformativeMessage && (showDelayWarning ? (
        <Typography variant='h3' className={styles.delayWarning}>
          <FormattedMessage {...messages.delayWarning} />
        </Typography>
      ) : informativeMessage && (
        <Typography variant='h3' className={styles.delayWarning}>
          {informativeMessage}
        </Typography>
      ))}

      <header className={styles.header}>
        <nav className={styles.headerContent}>
          <div className={styles.leftSide}>
            <RedeemLinkContainer
              redirect={homePageRedirect()}
              reserveRedirect={oldCBRedirect()}
              className={styles.link}
            >
              <StaticImage
                src={isLogosFromCBEOn && mainLogoFromCBE?.url.href
                  ? mainLogoFromCBE?.url.href
                  : versionDependent.logo.src}
                alt={isLogosFromCBEOn && mainLogoFromCBE?.alt
                  ? mainLogoFromCBE?.alt
                  : intl.formatMessage(messages.logoImageAlt)}
                className={styles.logo}
                width={200}
                height={80}
              />
            </RedeemLinkContainer>
          </div>

          <div className={styles.rightSide}>
            <RedeemLinkContainer
              redirect={customerSupportPageRedirect()}
              className={styles.link}
            >
              <>
                {layoutExtension === LayoutExtension.Mobile ? (
                  <CircleWithQuestionMark
                    fill={'var(--primary-icon-color)'}
                    className={styles.icon}
                  />
                ) : (
                  <Typography
                    variant='bodySubText1'
                    className={styles.customerSupport}
                  >
                    <FormattedMessage {...messages.customerSupportLink} />
                  </Typography>
                )}
              </>
            </RedeemLinkContainer>
            <FeatureToggleWrapContainer>
              {({ isRedeemLanguageSwitcher }: FeatureToggleValues) => (
                <>
                  {
                    isClient && isShowLanguageSwitcher
                    && isRedeemLanguageSwitcher ? (
                        <StoreSwitcherButtonContainer
                          label={storeSwitcherButtonLabel}
                          isNonBlockPart
                          isHeaderPart
                          textClassName={styles.textClassName}
                          iconFill='var(--secondary-link-color)'
                        />
                      ) : null
                  }
                </>
              )}
            </FeatureToggleWrapContainer>
          </div>
        </nav>
        {!!versionDependent.headerDecoration && <HeaderDecoration />}
      </header>
    </div>
  );
});

RedeemHeader.displayName = 'RedeemHeader';
